import sha1 from 'crypto-js/sha1';
import { get } from 'svelte/store';

import { format } from 'src/services/i18n';
import { clearToken, get_user_pin, pullDataWithLoading, standardLogin } from 'src/services/http';
import { isMultiUnitResult, nativeLogin } from 'src/services/auth_v2';
import { socket_disconnect } from 'src/services/socket';
import { invalidateAccountSessions } from 'src/services/unit_token_cache';
import { currentUnitId } from 'src/stores/account/home';
import { user_data, user_settings } from 'src/stores/account/user';
import { profiles } from 'src/stores/features/profiles';
import { aiSceneChatStore } from 'src/stores/features/ai-scene-chat';
import { loadingData } from 'src/stores/ui/loading-data';
import { toast } from '@Shelly/shelly-design-system';

import { clearDataOnLogin, handleLogout } from 'src/helpers/account_helpers';
import { normalizeEmail } from 'src/helpers/email_helpers';
import { isSubUnit } from 'src/helpers/user_helpers';
import { cameraServicesUnload } from 'src/components/Camera/helpers/camera-cloud';

const showPasswordChangedToast = () => {
	toast.open({
		type: 'success',
		title: get(format)('toast_password_saved_successfully', {
			default: 'Password saved successfully.',
		}),
	});
};

const persistProfilePassword = (email: string, hashPass: string) => {
	const activeProfile = get(profiles).find(
		(item) => normalizeEmail(item.email) === normalizeEmail(email),
	);
	if (!activeProfile) {
		return;
	}

	profiles.edit({
		...activeProfile,
		pass: hashPass,
	});
};

const logoutAfterPasswordChange = async () => {
	showPasswordChangedToast();
	await handleLogout(undefined, false, { forceToLogin: true });
};

export const reloginAfterPasswordChange = async (newPass: string): Promise<void> => {
	const hashPass = sha1(newPass).toString();
	const email = get(user_data).email;
	const unitId = get(currentUnitId) || String(get(user_data).user_id || '');
	const subId = isSubUnit() ? String(get(user_settings).sub) : undefined;

	persistProfilePassword(email, hashPass);

	try {
		invalidateAccountSessions(email);
		cameraServicesUnload();
		socket_disconnect();
		clearToken();

		const requestedUnitId = unitId || undefined;
		const res = await standardLogin(email, hashPass, requestedUnitId, subId);
		const sessionToken = res?.data?.token;
		const sessionReady =
			Boolean(res?.isok && sessionToken) && (!isMultiUnitResult(res) || Boolean(requestedUnitId));

		if (sessionReady) {
			nativeLogin(email, hashPass, res.data.user_api_url, sessionToken, res.data.lang);
			clearDataOnLogin();
			loadingData.set(false);
			await pullDataWithLoading();
			get_user_pin();
			aiSceneChatStore.loadChatPreferences();
			showPasswordChangedToast();
			return;
		}
	} catch (error) {
		console.error('[AUTH] Relogin after password change failed:', error);
	}

	await logoutAfterPasswordChange();
};
